import { EditOutlined } from "@ant-design/icons";
import { Button, Card, Empty, Form, Input, Popconfirm } from "antd";
import dayjs from "dayjs";
import capitalize from "lodash/capitalize";
import isEmpty from "lodash/isEmpty";
import lowerCase from "lodash/lowerCase";
import uniq from "lodash/uniq";
import { CSSProperties, FC, useEffect, useMemo, useRef, useState } from "react";
import { AiFillCaretRight } from "react-icons/ai";
import {
  BsChatDots,
  BsFillHandThumbsUpFill,
  BsFlag,
  BsHandThumbsUp,
  BsTrash,
} from "react-icons/bs";
import { HiCheckCircle, HiLockClosed } from "react-icons/hi";
import { useInView } from "react-intersection-observer";

import CardUserProfile from "@/components/Card/card-user-profile";
import Images from "@/components/Images";
import RedFlagModal, { IDataRedFlag } from "@/components/RedFlagModal";
import Spinner from "@/components/Spinner";
import useWordsConversionEnv from "@/hooks/useWordsConversionEnv";
import ModalPostEdit from "./ModalPostEdit";
import ProfileCardComment from "./ProfileCardComment";

import { useAnalytics } from "@/hooks/useAnalytics";
import { useGetCircle } from "@/hooks/useCircles";
import { useIsViewAsHR } from "@/hooks/useIsViewAsHR";
import {
  useCreateComment,
  useDeletePost,
  useGetCommentByPost,
  useUpdatePost,
} from "@/hooks/usePost";
import useAuthStore from "@/store/useAuthStore";
import { IPayloadCreateComment, IUpdatePost } from "@/types";
import { convertTextContainingLink, formatNumberKM } from "@/utils/helper";

export const ProfileCardPosted: FC<{
  dataPost: any;
  dataContext?: any;
  dataUser?: any;
  onSuccess?: any;
  onError?: any;
  type?: any;
  isVisiting?: boolean;
  className?: string;
  cardStyle?: CSSProperties;
}> = ({
  dataPost,
  dataUser,
  onSuccess,
  onError,
  dataContext,
  type = "USER",
  isVisiting,
  className,
  cardStyle,
}) => {
  const [form] = Form.useForm();

  const authUser = useAuthStore.useUser();

  const { isViewAsHR } = useIsViewAsHR();

  const [isComment, setIsComment] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [likeCount, setLikeCount] = useState<number>(
    uniq(dataPost.likeUsers).length || 0
  );
  const [isLiked, setIsLiked] = useState<boolean>(
    dataPost?.likeUsers?.includes(authUser?.profile?.user_id) || false
  );

  const [isProcessLike, setIsProcessLike] = useState<boolean>(false);
  const [isProcessDelete, setIsProcessDelete] = useState<boolean>(false);
  const [isProcessComment, setIsProcessComment] = useState<boolean>(false);

  const contentRef = useRef<HTMLDivElement>(null);
  const [isContentClamped, isContentClampedSet] = useState<boolean>(true);
  const [isContentExpanded, isContentExpandedSet] = useState<boolean>(false);

  const { convertWords } = useWordsConversionEnv();
  const { track } = useAnalytics();

  useEffect(() => {
    if (contentRef.current) {
      const { clientHeight, scrollHeight } = contentRef.current;
      isContentClampedSet(clientHeight < scrollHeight);
    }
  }, [contentRef.current, isContentExpanded]);

  const [isOpenModalRedFlag, setIsOpenModalRedFlag] = useState<{
    isOpen: boolean;
    referenceType: IDataRedFlag["referenceType"];
    referenceId: string;
  }>({
    referenceType: "EVENT",
    isOpen: false,
    referenceId: "",
  });

  const {
    data: dataComment,
    isLoading: isLoadingComment,
    refetch: refetchComment,
  } = useGetCommentByPost(dataPost?.id, false);

  const isOwner: boolean = useMemo(() => {
    return dataPost?.author?.id === authUser?.profile?.user_id;
  }, [dataPost]);

  const handleLikePost = () => {
    setIsLiked((liked) => {
      setIsProcessLike(true);
      setLikeCount((count) => count + (isLiked ? -1 : 1));
      postLikePost(!liked, dataPost);
      return !liked;
    });
  };

  const postLikePost = async (isLiked: boolean, dataPost: any) => {
    const dataLikeUsersCurrent: string[] = uniq(dataPost?.likeUsers) || [];
    let dataLikeUsers: string[] = [];

    setIsProcessLike(true);

    try {
      dataLikeUsers = !isLiked
        ? dataLikeUsersCurrent?.filter(
            (id: string) => id !== authUser?.profile?.user_id
          )
        : [...dataLikeUsersCurrent, authUser?.profile?.user_id];

      const payload: IUpdatePost = {
        id: dataPost?.id,
        _version: dataPost?._version,
        likeUsers: dataLikeUsers,
      };

      await useUpdatePost(payload);

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      setLikeCount((count) => count - (isLiked ? 1 : 0));
      setIsLiked((liked) => !liked);
      if (onError) {
        onError();
      }
    } finally {
      setIsProcessLike(false);
    }
  };

  const handleDeletePost = async () => {
    setIsProcessDelete(true);

    try {
      const payload: IUpdatePost = {
        id: dataPost?.id,
        _version: dataPost?._version,
      };

      await useDeletePost(payload);
      track("Delete Post", "Click", "Success");
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      track("Delete Post", "Click", "Failed");
      if (onError) {
        onError();
      }
    } finally {
      setIsProcessDelete(false);
    }
  };

  const handleCreateComment = async (values: any) => {
    track(`Post ${type || "Regular Post"} Commented`, "Click", "Success");
    setIsProcessComment(true);

    try {
      const payload: IPayloadCreateComment = {
        message: values?.content,
        commentPostId: dataPost?.id,
        commentAuthorId: authUser?.profile?.user_id,
        organizationGroup: authUser?.profile?.["cognito:groups"]?.[0],
      };

      if (isViewAsHR) {
        payload.isAuthorHR = true;
      }

      await useCreateComment(payload);
      track("Create Comment", "Submit", "Success");
      form.resetFields();
      refetchComment();
    } catch (error: any) {
      track("Create Comment", "Submit", "Failed");
    } finally {
      setIsProcessComment(false);
    }
  };

  useEffect(() => {
    if (isComment) refetchComment();
  }, [isComment]);

  const departmentWords = convertWords("department", { capitalize: true });

  const audience = useMemo(() => {
    if (dataPost?.audienceType === "PUBLIC") return "Everyone";
    if (dataPost?.audienceType === "PRIVATE")
      return isVisiting
        ? `${dataUser?.user?.firstName || dataUser?.user?.lastName}'s Page Only`
        : "My Page Only";
    if (dataPost?.audienceType === "DEPARTMENT")
      return `${dataPost?.author?.department?.name} ${departmentWords}`;
    if (dataPost?.audienceType === "CIRCLE") return "CIRCLE";
    return isVisiting
      ? `${dataUser?.user?.firstName || dataUser?.user?.lastName}'s Page Only`
      : "My Page Only";
  }, [dataPost]);

  const {
    data: dataGetCircle,
    refetch: refetchGetCircle,
    isFetching: isLoadingGetCircle,
  } = useGetCircle(audience === "CIRCLE" ? dataPost?.audienceID : "", false);

  useEffect(() => {
    if (audience === "CIRCLE") {
      refetchGetCircle();
    }
  }, [audience]);

  const [orientation, setOrientation] = useState<
    "landscape" | "portrait" | "square"
  >("landscape");

  const [primaryColor, setPrimaryColor] = useState<string>("");
  const isDataAttachmentExcel = ["xls", "xlsx"].some((ext) =>
    dataPost?.attachments?.[0]?.endsWith(ext)
  );

  const isPdf = useMemo(() => {
    return dataPost?.attachments?.[0]?.endsWith(".pdf");
  }, [dataPost?.attachments]);

  const imageComponent = useMemo(() => {
    if (dataPost?.attachments?.length === 0) return null;
    return (
      <div className="relative">
        {
          // if pdf
          isPdf && (
            <div className="flex justify-center items-center">
              <Images
                className="inline-block object-contain z-[1] w-full h-full"
                src={dataPost.attachments[0]}
                width={660}
                height={990}
                alt="image card"
              />
            </div>
          )
        }
        {
          // this code apply for not pdf
          // pdf don't need background color
          orientation !== "landscape" && !isPdf && (
            <div
              className="aspect-5/2"
              style={{ backgroundColor: primaryColor }}
            />
          )
        }
        {
          // if landscape & not pdf
          orientation === "landscape" && !isPdf && (
            <Images
              className={`inline-block object-cover aspect-5/2 ${orientation === "landscape" ? "" : "absolute opacity-0"}`}
              src={dataPost?.attachments?.[0]}
              width={1100}
              height={660}
              alt="image card"
              setOrientation={setOrientation}
              setPrimaryColor={setPrimaryColor}
              imageForPreview={true}
            />
          )
        }
        {
          // if potrait & not pdf
          orientation !== "landscape" && !isPdf && primaryColor && (
            <div className="absolute left-0 top-0 right-0 bottom-0 flex justify-center items-center">
              <Images
                className="inline-block object-contain z-[1] w-full h-full"
                src={dataPost?.attachments?.[0]}
                width={660}
                height={990}
                alt="image card"
                imageForPreview={true}
              />
            </div>
          )
        }
      </div>
    );
  }, [dataPost?.attachments, orientation, isPdf]);

  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  useEffect(() => {
    if (inView) {
      track("The Tea post viewed", "View", `Post ID: ${dataPost?.id}`);
    }
  }, [inView]);

  return (
    <>
      <div className={className}>
        {type == "THE_TEA" && (
          <div style={{ background: "#1A1A1A" }} className="p-5" ref={ref}>
            <CardUserProfile
              item={{
                id: dataPost?.author?.id,
                avatarUrl: dataPost?.author?.avatarUrl,
                ...dataPost?.author,
              }}
              variant="horizontal"
              points={dataPost?.author?.points}
              cgPercent={
                dataPost?.author?.id === authUser?.bio?.user?.id ? 100 : null
              }
              findCG={dataPost?.author?.id !== authUser?.bio?.user?.id}
              hideType
              avatarSize="sm"
            />
          </div>
        )}
        <Card
          style={{ ...cardStyle }}
          className="flex flex-col"
          actions={[
            <Button
              key="like"
              icon={
                isLiked ? (
                  <BsFillHandThumbsUpFill key="like" />
                ) : (
                  <BsHandThumbsUp key="like" />
                )
              }
              type="text"
              loading={isProcessLike}
              onClick={() => {
                handleLikePost();
                track(
                  `Like Post ${type || "Regular Post"}`,
                  "Click",
                  isLiked ? "Unlike" : "Like"
                );
              }}
              style={{ fontWeight: isLiked ? 700 : 400 }}
            >
              {likeCount > 0 && formatNumberKM(likeCount)} Like
              {likeCount > 1 && "s"}
            </Button>,
            <Button
              key="comment"
              icon={<BsChatDots key="comment" />}
              type="text"
              onClick={() => {
                setIsComment((prev) => !prev);
                track(
                  `Comment Post ${type || "Regular Post"} Clicked`,
                  "Click",
                  "opening comment section"
                );
              }}
            >
              Comments
            </Button>,
            (type === "THE_TEA" && isOwner) || !isVisiting ? (
              <Button
                icon={<EditOutlined key="edit" />}
                type="text"
                onClick={() => setIsEdit((prev) => !prev)}
              >
                Edit
              </Button>
            ) : null,
            !isOwner || isViewAsHR ? (
              <Button
                icon={<BsFlag key="report" />}
                type="text"
                onClick={() => {
                  setIsOpenModalRedFlag({
                    isOpen: true,
                    referenceType: "USER",
                    referenceId: dataUser?.id,
                  });
                  track(
                    `Report Post ${type || "Regular Post"} Clicked`,
                    "Click",
                    dataPost?.id
                  );
                }}
              >
                Report
              </Button>
            ) : (
              <Popconfirm
                title="Delete Post"
                description="Are you sure you want to delete this post?"
                okText="Yes"
                cancelText="No"
                onConfirm={() => handleDeletePost()}
              >
                <Button
                  icon={<BsTrash key="delete" />}
                  type="text"
                  loading={isProcessDelete}
                >
                  Delete
                </Button>
              </Popconfirm>
            ),
          ]?.filter((x) => x !== null)}
          cover={
            <>
              {isEmpty(dataPost?.attachments?.[0]) ? null : imageComponent}
              {audience && (
                <div
                  style={{ display: "flex" }}
                  className="items-center justify-between mt-4 w-full px-4"
                >
                  {isLoadingGetCircle ? (
                    <Spinner size="sm" isTextLoading={false} />
                  ) : (
                    <p className="text-xs text-yellow">
                      {audience === "CIRCLE"
                        ? `${dataGetCircle?.name} Circle`
                        : audience}
                    </p>
                  )}
                  <p className="text-xs text-[#D9D9D9]">
                    {dayjs(dataPost?.createdAt).format(
                      "MMMM DD, YYYY [@]h:mma"
                    )}
                  </p>
                </div>
              )}
            </>
          }
        >
          <div
            className={`grid gap-4 mb-5 grid-cols-1 ${
              type !== "THE_TEA" ? "lg:grid-cols-2" : ""
            }`}
          >
            {type !== "THE_TEA" && (
              <div>
                <CardUserProfile
                  item={{
                    id: dataPost?.author?.id,
                    avatarUrl: dataPost?.author?.avatarUrl,
                    ...dataPost?.author,
                  }}
                  variant="horizontal"
                  points={dataPost?.author?.points}
                  cgPercent={
                    dataPost?.author?.id === authUser?.bio?.user?.id
                      ? 100
                      : null
                  }
                  findCG={dataPost?.author?.id !== authUser?.bio?.user?.id}
                  hideType
                  avatarSize="sm"
                />
              </div>
            )}
            {/* {type == "THE_TEA" && (
              <div className="flex justify-between">
                <p className="text-yellow">
                  {dataPost?.audienceType === "PUBLIC"
                    ? "Everyone"
                    : _.capitalize(dataPost?.audienceType)}
                </p>{" "}
                <p style={{ color: "#d9d9d9" }} className="text-right">
                  {dayjs(dataPost?.createdAt).format("MMMM DD, YYYY [@]hha")}
                </p>{" "}
              </div>
            )} */}

            {dataContext && (
              <div>
                <CardUserProfile
                  type={lowerCase(dataContext?.type) as never}
                  squareAvatar
                  item={{
                    avatarUrl: dataContext?.imageUrl,
                    firstName: dataContext?.name,
                    id: dataContext?.id,
                    ...dataContext,
                  }}
                  findCGCTX={
                    dataContext?.type as "CIRCLE" | "EVENT" | undefined
                  }
                  variant="horizontal"
                  avatarSize="sm"
                  reference={`${dataContext?.type}-activity`}
                  customType={
                    <p className="text-neutral-300">
                      {dataContext?.privacy === "PUBLIC" ? (
                        <HiCheckCircle className="inline-block text-green-400 mr-1" />
                      ) : (
                        <HiLockClosed className="inline-block mr-1" />
                      )}
                      <span>
                        {capitalize(lowerCase(dataContext?.privacy))}{" "}
                        {capitalize(lowerCase(dataContext?.type))}{" "}
                      </span>
                    </p>
                  }
                />
              </div>
            )}
          </div>

          <div className="bg-neutral-800 rounded px-3 py-5 break-words whitespace-pre-wrap">
            <div
              ref={contentRef}
              className={`${!isContentExpanded && "line-clamp-3"}`}
              dangerouslySetInnerHTML={{
                __html: convertTextContainingLink(dataPost?.content),
              }}
            />
            {(isContentClamped || isContentExpanded) && (
              <span
                role="button"
                onClick={() => {
                  track(
                    "Read More Post",
                    "Click",
                    isContentExpanded ? "Read Less" : "Read More"
                  );
                  isContentExpandedSet((prev) => !prev);
                }}
                className="text-yellow"
              >
                {isContentExpanded ? "Read Less" : "Read More"}
              </span>
            )}
          </div>
        </Card>

        {isComment && (
          <div className="bg-neutral-950 block">
            <div className="px-4 py-5 max-h-96 overflow-auto grid grid-cols-1 gap-6">
              {isLoadingComment ? (
                <>
                  <div className="animate-pulse">
                    <div className="w-full h-16 bg-neutral-700 rounded-lg" />
                  </div>
                  <div className="animate-pulse">
                    <div className="w-full h-16 bg-neutral-700 rounded-lg" />
                  </div>
                </>
              ) : isEmpty(dataComment) ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
              ) : (
                dataComment?.map((item: any, idx: number) => (
                  <ProfileCardComment
                    dataComment={item}
                    key={`list-of-comment-${dataPost?.id}-${idx}`}
                    handleOpenModalRedFlag={() =>
                      setIsOpenModalRedFlag({
                        isOpen: true,
                        referenceType: "EVENT",
                        referenceId: dataUser?.id,
                      })
                    }
                    onSuccess={() => {
                      refetchComment();
                    }}
                  />
                ))
              )}
            </div>

            <Form form={form} onFinish={handleCreateComment} layout="vertical">
              <div className="px-4 py-5 flex gap-4 items-center w-full bg-neutral-900">
                <div>
                  <CardUserProfile
                    item={authUser?.profile}
                    avatarOnly
                    avatarSize="xs"
                    points={authUser?.bio?.user?.points}
                    cgPercent={100}
                  />
                </div>
                <div className="flex-1">
                  <Form.Item
                    name={"content"}
                    rules={[{ required: true }]}
                    style={{ margin: 0 }}
                  >
                    <Input.TextArea
                      maxLength={250}
                      placeholder="Please Comment ..."
                      style={{ resize: "none", width: "100%" }}
                      disabled={isProcessComment}
                      allowClear
                      rows={2}
                    />
                  </Form.Item>
                </div>
                <div>
                  <Button
                    type="primary"
                    shape="circle"
                    loading={isProcessComment}
                    htmlType="submit"
                    icon={<AiFillCaretRight className="text-2xl" />}
                    size={"large"}
                  />
                </div>
              </div>
            </Form>
          </div>
        )}
      </div>

      <ModalPostEdit
        onCancel={() => setIsEdit(false)}
        open={isEdit}
        data={{
          content: dataPost?.content,
          image: dataPost?.attachments?.[0],
          id: dataPost?.id,
          _version: dataPost?._version,
        }}
        onSuccessEdit={() => {
          onSuccess();
          setIsEdit(false);
        }}
      />

      {isOpenModalRedFlag.isOpen && (
        <RedFlagModal
          onClose={() =>
            setIsOpenModalRedFlag({
              isOpen: false,
              referenceType: "EVENT",
              referenceId: "",
            })
          }
          isOpen={isOpenModalRedFlag.isOpen}
          successMessage="You have successfully reported this matter. "
          errorMessage="Red flag report failed!"
          referenceType={isOpenModalRedFlag.referenceType}
          referenceId={isOpenModalRedFlag?.referenceId}
        />
      )}
    </>
  );
};

export default ProfileCardPosted;
